export const LANDING_SOURCE_INTENT_MAX_AGE_MS = 30 * 60 * 1000;

const LANDING_SOURCE_INTENT_KEY = "topclip.landingSourceIntent.v1";
const MAX_SOURCE_URL_LENGTH = 2_048;
const CANONICAL_YOUTUBE_HOST = "www.youtube.com";
const CANONICAL_YOUTUBE_ORIGIN = `https://${CANONICAL_YOUTUBE_HOST}`;
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
]);
const SHORT_YOUTUBE_HOST = "youtu.be";

type LandingSourceIntentRecord = {
  version: 1;
  url: string;
  createdAt: number;
  ownerId: string | null;
};

export type LandingYoutubeUrlValidation =
  | { ok: true; url: string }
  | { ok: false; message: string };

const visibleAscii = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x21 || code > 0x7e) return false;
  }
  return true;
};

type RawUrlParts = {
  authority: string;
  path: string;
  query: string;
};

const rawUrlParts = (candidate: string): RawUrlParts | null => {
  const schemeSeparator = candidate.indexOf("://");
  if (
    schemeSeparator === -1 ||
    candidate.slice(0, schemeSeparator).toLowerCase() !== "https"
  ) {
    return null;
  }
  const remainder = candidate.slice(schemeSeparator + 3);
  const authorityEnd = remainder.search(/[/?#]/);
  const authority =
    authorityEnd === -1 ? remainder : remainder.slice(0, authorityEnd);
  if (!authority) return null;

  const pathAndSuffix =
    authorityEnd === -1 ? "" : remainder.slice(authorityEnd);
  const queryStart = pathAndSuffix.indexOf("?");
  const fragmentStart = pathAndSuffix.indexOf("#");
  let pathEnd = pathAndSuffix.length;
  if (queryStart !== -1) pathEnd = queryStart;
  if (fragmentStart !== -1 && fragmentStart < pathEnd) pathEnd = fragmentStart;
  return {
    authority,
    path: pathAndSuffix.slice(0, pathEnd),
    query:
      queryStart !== -1 &&
      (fragmentStart === -1 || queryStart < fragmentStart)
        ? pathAndSuffix.slice(
            queryStart + 1,
            fragmentStart === -1 ? pathAndSuffix.length : fragmentStart
          )
        : "",
  };
};

const canonicalYoutubeUrl = (videoId: string): string =>
  `${CANONICAL_YOUTUBE_ORIGIN}/watch?v=${videoId}`;

export const validateLandingYoutubeUrl = (
  candidate: unknown
): LandingYoutubeUrlValidation => {
  if (typeof candidate !== "string" || candidate === "") {
    return { ok: false, message: "Paste a YouTube link to continue." };
  }

  if (
    candidate !== candidate.trim() ||
    candidate.length > MAX_SOURCE_URL_LENGTH ||
    !visibleAscii(candidate)
  ) {
    return { ok: false, message: "Enter a complete YouTube video link." };
  }

  const rawParts = rawUrlParts(candidate);
  if (
    rawParts === null ||
    (!YOUTUBE_HOSTS.has(rawParts.authority) &&
      rawParts.authority !== SHORT_YOUTUBE_HOST)
  ) {
    return { ok: false, message: "Enter a complete YouTube video link." };
  }

  const rawSearchParams = new URLSearchParams(rawParts.query);
  if (
    rawSearchParams.has("list") ||
    rawSearchParams.has("playlist_id")
  ) {
    return {
      ok: false,
      message: "Paste one YouTube video link without a playlist.",
    };
  }

  if (
    (YOUTUBE_HOSTS.has(rawParts.authority) && rawParts.path !== "/watch") ||
    (rawParts.authority === SHORT_YOUTUBE_HOST &&
      (rawParts.path.length !== 12 ||
        rawParts.path[0] !== "/" ||
        !VIDEO_ID.test(rawParts.path.slice(1))))
  ) {
    return { ok: false, message: "Enter a complete YouTube video link." };
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, message: "Enter a complete YouTube video link." };
  }

  const authority = rawParts.authority;
  if (
    parsed.protocol !== "https:" ||
    parsed.hostname !== authority ||
    parsed.port !== "" ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    candidate.includes("#")
  ) {
    return { ok: false, message: "Enter a complete YouTube video link." };
  }

  if (
    parsed.searchParams.has("list") ||
    parsed.searchParams.has("playlist_id")
  ) {
    return {
      ok: false,
      message: "Paste one YouTube video link without a playlist.",
    };
  }

  let videoId: string | null = null;
  if (authority === SHORT_YOUTUBE_HOST) {
    const pathParts = parsed.pathname.split("/");
    if (pathParts.length === 2 && pathParts[0] === "") {
      videoId = pathParts[1];
    }
  } else if (YOUTUBE_HOSTS.has(authority) && parsed.pathname === "/watch") {
    const videoIds = parsed.searchParams.getAll("v");
    if (videoIds.length === 1) videoId = videoIds[0];
  }

  if (!videoId || !VIDEO_ID.test(videoId)) {
    return {
      ok: false,
      message: "Paste a supported YouTube video link, not a playlist or channel.",
    };
  }
  return { ok: true, url: canonicalYoutubeUrl(videoId) };
};

export const extractLandingYoutubeVideoId = (
  candidate: unknown
): string | null => {
  const validation = validateLandingYoutubeUrl(candidate);
  if (!validation.ok) return null;
  return new URL(validation.url).searchParams.get("v");
};

const isOwnerId = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

export const clearLandingSourceIntent = (): void => {
  try {
    sessionStorage.removeItem(LANDING_SOURCE_INTENT_KEY);
  } catch {
    // The route still works when storage is unavailable.
  }
};

const readStoredIntent = (
  nowMilliseconds = Date.now()
): LandingSourceIntentRecord | null => {
  try {
    const rawValue = sessionStorage.getItem(LANDING_SOURCE_INTENT_KEY);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as Partial<LandingSourceIntentRecord>;
    const validation = validateLandingYoutubeUrl(value.url);
    const createdAt = Number(value.createdAt);
    const age = nowMilliseconds - createdAt;
    const ownerIsValid = value.ownerId === null || isOwnerId(value.ownerId);
    if (
      value.version !== 1 ||
      !validation.ok ||
      !Number.isFinite(createdAt) ||
      createdAt <= 0 ||
      age < 0 ||
      age > LANDING_SOURCE_INTENT_MAX_AGE_MS ||
      !ownerIsValid
    ) {
      clearLandingSourceIntent();
      return null;
    }
    return {
      version: 1,
      url: validation.url,
      createdAt,
      ownerId: value.ownerId ?? null,
    };
  } catch {
    clearLandingSourceIntent();
    return null;
  }
};

const writeIntent = (intent: LandingSourceIntentRecord): void => {
  try {
    sessionStorage.setItem(LANDING_SOURCE_INTENT_KEY, JSON.stringify(intent));
  } catch {
    // The prefill is optional when browser storage is unavailable.
  }
};

export const storeLandingSourceIntent = (
  candidate: unknown,
  ownerId: string | null,
  nowMilliseconds = Date.now()
): LandingSourceIntentRecord | null => {
  const validation = validateLandingYoutubeUrl(candidate);
  if (!validation.ok) return null;
  const intent: LandingSourceIntentRecord = {
    version: 1,
    url: validation.url,
    createdAt: nowMilliseconds,
    ownerId: isOwnerId(ownerId) ? ownerId : null,
  };
  writeIntent(intent);
  return intent;
};

export const readLandingSourceIntent = (
  nowMilliseconds = Date.now()
): Readonly<LandingSourceIntentRecord> | null =>
  readStoredIntent(nowMilliseconds);

export const claimLandingSourceIntent = (
  ownerId: unknown,
  nowMilliseconds = Date.now()
): Readonly<LandingSourceIntentRecord> | null => {
  if (!isOwnerId(ownerId)) {
    clearLandingSourceIntent();
    return null;
  }
  const intent = readStoredIntent(nowMilliseconds);
  if (!intent) return null;
  if (intent.ownerId !== null && intent.ownerId !== ownerId) {
    clearLandingSourceIntent();
    return null;
  }
  if (intent.ownerId === null) {
    const claimedIntent = { ...intent, ownerId };
    writeIntent(claimedIntent);
    return claimedIntent;
  }
  return intent;
};

export const clearLandingSourceIntentForOwner = (ownerId: unknown): void => {
  if (!isOwnerId(ownerId)) return;
  const intent = readStoredIntent();
  if (intent?.ownerId === ownerId) clearLandingSourceIntent();
};
